import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { addDoc, collection, collectionData, doc, docData, DocumentData, Firestore, getDoc, setDoc } from '@angular/fire/firestore';
import { BehaviorSubject, combineLatest, EMPTY, filter, from, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { Tournament } from '../tournament';
import { Player } from '../model/player';
import { deleteDoc, getDocs, updateDoc } from 'firebase/firestore';
import { environment } from '../../../../environments/environment';
import { Match, MatchExtended } from '../model/match';
import { TournamentCategory } from '../tournament-category';
import { CategoryFinal } from '../category-final';
import { stripUndefined, stripUndefinedDeep } from '../utils/object-utils';

@Injectable({
  providedIn: 'root',
})
export class OpenSquashService {
  public tournamentId = 'tu4I1g3CgO8WCvAiIcmA'; // Id del torneo
  matchesCollection = 'matches-iv-valida';
  playersCollection = 'players-iv-valida';
  controlOrderDocPath = '';

  private players!: Player[];
  private currentTournament!: Tournament;

  private firestore = inject(Firestore);
  private envInjector = inject(EnvironmentInjector);

  tournament$!: Observable<Tournament>;
  matches$!: Observable<Match[]>;
  controlOrder$!: Observable<Record<string, number>>;
  
  private esAdminSubject = new BehaviorSubject<boolean>(
    localStorage.getItem('auth') === environment.tokenAuth
  );
  

  constructor() {

  }

  setTournament(idTournament: string) {
    this.tournamentId = idTournament;
    if (idTournament === '7mo-open') {
      this.matchesCollection = 'matches-7mo-open';
      this.playersCollection = 'players-7mo-open';
    } else if (idTournament === 'tu4I1g3CgO8WCvAiIcmA') {
      this.matchesCollection = 'matches-iv-valida';
      this.playersCollection = 'players-iv-valida';
    } else if (idTournament === 'conc-juvenil-2025') {
      this.matchesCollection = 'matches-conc-juvenil-2025';
      this.playersCollection = 'players-conc-juvenil-2025';
    } else if (idTournament === 'v-valida-departamental') {
      this.matchesCollection = 'matches-v-valida-departamental';
      this.playersCollection = 'players-v-valida-departamental';
    } else if (idTournament === '8open') {
      this.matchesCollection = 'matches-8open';
      this.playersCollection = 'players-8open';
    } else if (idTournament === '9open') {
      this.matchesCollection = 'matches-9open';
      this.playersCollection = 'players-9open';
    } else if (idTournament === '10open') {
      this.matchesCollection = 'matches-10open';
      this.playersCollection = 'players-10open';
    } else if (idTournament === '11open') {
      this.matchesCollection = 'matches-11open';
      this.playersCollection = 'players-11open';
    } else if (idTournament === '12open') {
      this.matchesCollection = 'matches-12open';
      this.playersCollection = 'players-12open';
    } else if (idTournament === '13open') {
      this.matchesCollection = 'matches-13open';
      this.playersCollection = 'players-13open';
    } else if (idTournament === '1open-femenino') {
      this.matchesCollection = 'matches-1open-femenino';
      this.playersCollection = 'players-1open-femenino';
    } else if (idTournament === 'i-valida-ant-2026') {
      this.matchesCollection = 'matches-i-valida-ant-2026';
      this.playersCollection = 'players-i-valida-ant-2026';
    }

    this.getPlayers().subscribe();

    runInInjectionContext(this.envInjector, () => {
      console.log('100. consultando torneo > ', this.tournamentId);
      this.tournament$ = docData(
        doc(this.firestore, `tournament/${this.tournamentId}`), { idField: 'id' }
      ).pipe(
        filter((data): data is any => data != null),
        map((data) => {
          this.currentTournament = this.transformToTournament(data)
          return this.currentTournament;
        }),
        // cachea el último valor y cierra la suscripción cuando no haya observers
        shareReplay({ bufferSize: 1, refCount: true })
      )

      this.matches$ = collectionData(
        collection(this.firestore, this.matchesCollection), { idField: 'id' }
      ).pipe(
        map((data) => {
          const dt = this.transformToMatchs(data);
          return dt;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      this.controlOrderDocPath = `control-order/${this.tournamentId}`;
      this.controlOrder$ = docData(
        doc(this.firestore, this.controlOrderDocPath)
      ).pipe(
        map((data: any) => (data?.order as Record<string, number>) || {}),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    });
  }

  getControlOrder$(): Observable<Record<string, number>> {
    return this.controlOrder$;
  }

  /**
   * Lee el número de canchas configuradas para el torneo desde localStorage.
   * Default: 4 si no hay valor o es inválido.
   */
  getTotalCourts(tournamentId?: string): number {
    const id = tournamentId || this.tournamentId;
    const stored = localStorage.getItem(`control_courts_${id}`);
    const n = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 4;
  }

  /**
   * Persiste el número de canchas del torneo en localStorage.
   * Fuente única de verdad usada por el tablero de control y el generador de QR.
   */
  setTotalCourts(n: number, tournamentId?: string): void {
    const id = tournamentId || this.tournamentId;
    localStorage.setItem(`control_courts_${id}`, n.toString());
  }

  saveControlOrder(order: Record<string, number>): Promise<void> {
    const ref = doc(this.firestore, this.controlOrderDocPath);
    return setDoc(ref, { order, updatedAt: Date.now() }, { merge: true });
  }

  get esAdmin$(): Observable<boolean> {
    return this.esAdminSubject.asObservable();
  }

  reloadInfo() {
    this.players = [];

    // this.getInfo().subscribe(); 
    this.getPlayers().subscribe();
    // this.getMatches().subscribe();
  }

  createPlayer(player: Player): Observable<Player> {
    const ref = doc(this.firestore, `${this.playersCollection}/${player.playerId}`);
    return from(setDoc(ref, player)).pipe(switchMap(() => of(player)));
  }

  createMatch(match: Match): Observable<Match> {
    const matchesCollection = collection(this.firestore, this.matchesCollection);
    return from(addDoc(matchesCollection, match)).pipe(
      switchMap((result) => {
        match.id = result.id;
        return of(match);
      })
    );
  }

  addPlayerToGroup(category: string, group: string, idPlayer: any): Observable<Tournament> {
    let tournament = { ... this.currentTournament };
    const categoryFound = tournament.categories.find(c => c.category === category);
    if (categoryFound) {
      const groupFound = categoryFound.groups.find(g => g.name === group);
      if (groupFound) {
        if (!groupFound.players) {
          groupFound.players = [];
        }
        const playerGroup = groupFound.players.find(p => p.playerId === idPlayer);
        if (!playerGroup) {
          groupFound.players.push({ playerId: idPlayer });
          return this.updateTournament(tournament);
        }
      }
    }
    return EMPTY;
  };

  setFinalPlayer(category: TournamentCategory, match: CategoryFinal, player: string) {
    // debugger;
    // let tournament = { ... this.currentTournament };
    // const categoryFound = tournament.categories.find(c => c.category === data.categoryType);
    // if (categoryFound) {
    //   const groupFound = categoryFound.groups.find(g => g.name === data.groupName);
    //   if (groupFound) {
    //     if (!groupFound.players) {
    //       groupFound.players = [];
    //     }
    //     const playerGroup = groupFound.players.find(p => p.playerId === data.playerId);
    //     if (playerGroup) {
    //       playerGroup.order = order;
    //       return this.updateTournament(tournament);
    //     }
    //   }
    // }
    // return EMPTY;
  }

  updateTournament(tournament: Tournament): Observable<Tournament> {
    const docRef = doc(this.firestore, `tournament/${this.tournamentId}`);
    return from(updateDoc(docRef, stripUndefinedDeep({ ...tournament }) as any)).pipe(
      switchMap(() => of(tournament))
    );
  }

  updateMatch(match: Match) {
    const docRef = doc(this.firestore, `${this.matchesCollection}/${match.id}`);
    return from(updateDoc(docRef, stripUndefined(match))).pipe(
      switchMap(() => of(match))
    );
  }

  deleteMatch(match: Match) {
    const docRef = doc(this.firestore, `${this.matchesCollection}/${match.id}`);
    return from(deleteDoc(docRef)).pipe(
      switchMap(() => of(match))
    );
  }

  getPlayers(): Observable<Player[]> {
    if (this.players && this.players.length > 0) {
      return of([...this.players]);
    }

    console.log('>>> SERVICE > Cargando players desde Firestore...');
    return runInInjectionContext(this.envInjector, () => {
      const colRef = collection(this.firestore, this.playersCollection);
      return from(getDocs(colRef)).pipe(
        map(snapshot => {
          this.players = snapshot.docs.map(doc => this.transformToPlayer(doc.id, doc.data()));
          return [...this.players];
        })
      );
    });
  }

  reloadPlayers() {
    this.players = [];
    this.getPlayers().subscribe();
  }

  getMatchesExtended(): Observable<MatchExtended[]> {
    return combineLatest([this.matches$, this.getPlayers()]).pipe(
      map(([matches, players]) => matches.map(match => ({
        ...match,
        player1: players.find(p => p.playerId === match.player1Id),
        player2: players.find(p => p.playerId === match.player2Id),
      }))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private transformToTournament(data: DocumentData | undefined): Tournament {
    return {
      id: data ? data['id'] : undefined,
      name: data ? data['name'] : undefined,
      categories: data && data['categories'] ? data['categories'] : [],
    } as Tournament;
  }

  private transformToPlayer(id: string, data: DocumentData | undefined): Player {
    return {
      id: id,
      playerId: id,
      playerLabel: data && data['playerLabel'] ? data['playerLabel'] : [],
      playerName: data && data['playerName'] ? data['playerName'] : '',
    } as Player;
  }

  private transformToMatchs(data: any[]): Match[] {
    return data.map(data => {
      return {
        id: data.id,
        code: data.code,
        state: data.state,
        tournamentId: data && data['tournamentId'] ? data['tournamentId'] : '',
        category: data && data['category'] ? data['category'] : [],
        groupName: data && data['groupName'] ? data['groupName'] : '',
        date: data && data['date'] ? data['date'].toDate() : undefined,
        player1Id: data && data['player1Id'] ? data['player1Id'] : undefined,
        player1GamesWon: data && data['player1GamesWon'] ? data['player1GamesWon'] : 0,
        player1GamesLost: data && data['player1GamesLost'] ? data['player1GamesLost'] : 0,
        player1PointsWon: data && data['player1PointsWon'] ? data['player1PointsWon'] : 0,
        player1PointsLost: data && data['player1PointsLost'] ? data['player1PointsLost'] : 0,
        player2Id: data && data['player2Id'] ? data['player2Id'] : undefined,
        player2GamesWon: data && data['player2GamesWon'] ? data['player2GamesWon'] : 0,
        player2GamesLost: data && data['player2GamesLost'] ? data['player2GamesLost'] : 0,
        player2PointsWon: data && data['player2PointsWon'] ? data['player2PointsWon'] : 0,
        player2PointsLost: data && data['player2PointsLost'] ? data['player2PointsLost'] : 0,
        games: data && data['games'] ? data['games'] : [],
        court: data && data['court'] ? data['court'] : '',
        playerIdWinner: data && data['playerIdWinner'] ? data['playerIdWinner'] : undefined,
        matchStartedAt: data['matchStartedAt'],
        matchEndedAt: data['matchEndedAt'],
        matchDurationSeconds: data['matchDurationSeconds'],
        bestOf: data['bestOf'] || 3,
        pointsToWin: data['pointsToWin'] || 11,
        gamesDetailed: data['gamesDetailed'],
        warmupSeconds: data['warmupSeconds'],
        restBetweenGamesSeconds: data['restBetweenGamesSeconds'],
        refereeName: data['refereeName'],
        } as Match;
    });
  }

  setAsAdmin(admin: boolean) {
    this.esAdminSubject.next(admin);
    if (!admin) {
      localStorage.removeItem('auth');
    }
  }

  updateResults(match: MatchExtended, category: TournamentCategory) {
    let tournament = { ... this.currentTournament };
    const categoryFound = tournament.categories.find(c => c.category === category.category);
    if (categoryFound) {
      if (match.groupName === 'finales') {
        const finals = categoryFound.finals;
        const quarterfinals = finals.quarterfinals || [];
        const semifinals = finals.semifinals || [];
        const final = finals.final;
        let finalGames = match.games ? [...match.games] : [];
        finalGames = finalGames.filter(g => 
          (g.player1Points && g.player1Points !== 0) || 
          (g.player2Points && g.player2Points !== 0)
        );

        // Elimina las propiedades 'player1' y 'player2' si existen en los objetos de finales
        [quarterfinals, semifinals].forEach(rounds => {
          rounds.forEach(match => {
            if ('player1' in match) {
              delete match.player1;
            }
            if ('player2' in match) {
              delete match.player2;
            }
          });
        });

        if (final) {
          if ('player1' in final) {
            delete final.player1;
          }
          if ('player2' in final) {
            delete final.player2;
          }
        }

        const qftm = quarterfinals.find(qfm => ((qfm.player1Id === match.player1Id && qfm.player2Id === match.player2Id) || (qfm.player1Id === match.player2Id && qfm.player2Id === match.player1Id)));
        if (qftm) {
          qftm.player1MatchesWon = match.player1GamesWon;
          qftm.player2MatchesWon = match.player2GamesWon;
          qftm.playerWinnerId = match.playerIdWinner || undefined;
          qftm.games = finalGames;

          if (qftm.order === 1) {
            if (semifinals[0]) {
              semifinals[0].player1Id = match.playerIdWinner || undefined;
            }
          } else if (qftm.order === 2) {
            if (semifinals[0]) {
              semifinals[0].player2Id = match.playerIdWinner || undefined;
            }
          } else if (qftm.order === 3) {
            if (semifinals[1]) {
              semifinals[1].player1Id = match.playerIdWinner || undefined;
            }
          } else if (qftm.order === 4) {
            if (semifinals[1]) {
              semifinals[1].player2Id = match.playerIdWinner || undefined;
            }
          }
        } else {
          const sftm = semifinals.find(sfm => ((sfm.player1Id === match.player1Id && sfm.player2Id === match.player2Id) || (sfm.player1Id === match.player2Id && sfm.player2Id === match.player1Id)));
          if (sftm) {
            sftm.player1MatchesWon = match.player1GamesWon;
            sftm.player2MatchesWon = match.player2GamesWon;
            sftm.playerWinnerId = match.playerIdWinner || undefined;
            sftm.games = finalGames;

            if (sftm.order === 1) {
              if (final) {
                final.player1Id = match.playerIdWinner || undefined;
              }
            } else if (sftm.order === 2) {
              if (final) {
                final.player2Id = match.playerIdWinner || undefined;
              }
            }
          } else {
            if (final && ((final.player1Id === match.player1Id && final.player2Id === match.player2Id) || (final.player1Id === match.player2Id && final.player2Id === match.player1Id))) {
              final.player1MatchesWon = match.player1GamesWon;
              final.player2MatchesWon = match.player2GamesWon;
              final.playerWinnerId = match.playerIdWinner || undefined;
              final.games = finalGames;
            }
          }
        }
      } else {
        const groupFound = categoryFound.groups.find(g => g.name === match.groupName);
        if (groupFound) {
          const players = groupFound.players || [];
          const player1 = players.find(p => p.playerId === match.player1Id);
          if (player1) {
            player1.matchesWon = (player1.matchesWon || 0) + (match.playerIdWinner === match.player1Id ? 1 : 0);
            player1.matchesLost = (player1.matchesLost || 0) + (match.playerIdWinner !== match.player1Id ? 1 : 0);
            player1.setsWon = (player1.setsWon || 0) + (match.player1GamesWon || 0);
            player1.setsLost = (player1.setsLost || 0) + (match.player1GamesLost || 0);
            player1.pointsWon = (player1.pointsWon || 0) + (match.player1PointsWon || 0);
            player1.pointsLost = (player1.pointsLost || 0) + (match.player1PointsLost || 0);
          }
          const player2 = players.find(p => p.playerId === match.player2Id);
          if (player2) {
            player2.matchesWon = (player2.matchesWon || 0) + (match.playerIdWinner === match.player2Id ? 1 : 0);
            player2.matchesLost = (player2.matchesLost || 0) + (match.playerIdWinner !== match.player2Id ? 1 : 0);
            player2.setsWon = (player2.setsWon || 0) + (match.player2GamesWon || 0);
            player2.setsLost = (player2.setsLost || 0) + (match.player2GamesLost || 0);
            player2.pointsWon = (player2.pointsWon || 0) + (match.player2PointsWon || 0);
            player2.pointsLost = (player2.pointsLost || 0) + (match.player2PointsLost || 0);
          }

          const sortedPlayers = [...players].sort((a, b) => {
            // Ordenar por matchesWon descendente
            const matchesDiff = (b.matchesWon || 0) - (a.matchesWon || 0);
            if (matchesDiff !== 0) return matchesDiff;

            // Si empate, ordenar por diferencia setsWon - setsLost descendente
            const setsDiffA = (a.setsWon || 0) - (a.setsLost || 0);
            const setsDiffB = (b.setsWon || 0) - (b.setsLost || 0);
            const setsDiff = setsDiffB - setsDiffA;
            if (setsDiff !== 0) return setsDiff;

            // Si empate, ordenar por diferencia pointsWon - pointsLost descendente
            const pointsDiffA = (a.pointsWon || 0) - (a.pointsLost || 0);
            const pointsDiffB = (b.pointsWon || 0) - (b.pointsLost || 0);
            return pointsDiffB - pointsDiffA;
          });

          // Asignar el orden comenzando en 1
          sortedPlayers.forEach((player, idx) => {
            player.order = idx + 1;
          });
        }
      }

      
      console.log('TOURNAMENT UPDATED', tournament);
      
      return this.updateTournament(tournament);
    }
    return EMPTY;
  }

  updatePositions(category: TournamentCategory) {
    let tournament = { ... this.currentTournament };
    const categoryFound = tournament.categories.find(c => c.category === category.category);
    if (categoryFound) {
      categoryFound.players = category.players;
      return this.updateTournament(tournament);
    }
    return EMPTY;
  }

  updateCategory(category: TournamentCategory) {
    let tournament = { ... this.currentTournament };
    let categoryFound = tournament.categories.find(c => c.category === category.category);

    const finals = categoryFound?.finals;
    if (finals) {
      const cleanMatch = (match: any) => {
        delete match.player1;
        delete match.player2;
        return stripUndefined(match);
      };

      if (finals.quarterfinals) {
        finals.quarterfinals = finals.quarterfinals.map(cleanMatch) as any;
      }
      if (finals.semifinals) {
        finals.semifinals = finals.semifinals.map(cleanMatch) as any;
      }
      if (finals.final) {
        finals.final = cleanMatch(finals.final) as any;
      }

      category.finals = finals;
    }

    console.log('torneo finals', tournament);


    if (categoryFound) {
      categoryFound  = category;
      return this.updateTournament(tournament);
    }
    return EMPTY;
  }

  // MÉTODOS PARA ARBITRAJE

  /**
   * Obtiene un partido por su código único
   * Busca en todas las colecciones de partidos hasta encontrarlo
   */
  getMatchByCode(code: string): Observable<MatchExtended | null> {
    const colRef = collection(this.firestore, this.matchesCollection);
    return from(getDocs(colRef)).pipe(
      map(snapshot => {
        const matchDoc = snapshot.docs.find(doc => doc.data()['code'] === code);
        if (!matchDoc) {
          return null;
        }
        const matchData = matchDoc.data();
        const match: Match = {
          id: matchDoc.id,
          code: matchData['code'],
          state: matchData['state'],
          tournamentId: matchData['tournamentId'] || '',
          category: matchData['category'] || '',
          groupName: matchData['groupName'] || '',
          date: matchData['date'] ? matchData['date'].toDate() : undefined,
          player1Id: matchData['player1Id'],
          player1GamesWon: matchData['player1GamesWon'] || 0,
          player1GamesLost: matchData['player1GamesLost'] || 0,
          player1PointsWon: matchData['player1PointsWon'] || 0,
          player1PointsLost: matchData['player1PointsLost'] || 0,
          player2Id: matchData['player2Id'],
          player2GamesWon: matchData['player2GamesWon'] || 0,
          player2GamesLost: matchData['player2GamesLost'] || 0,
          player2PointsWon: matchData['player2PointsWon'] || 0,
          player2PointsLost: matchData['player2PointsLost'] || 0,
          games: matchData['games'] || [],
          court: matchData['court'] || '',
          playerIdWinner: matchData['playerIdWinner'],
          matchStartedAt: matchData['matchStartedAt'],
          matchEndedAt: matchData['matchEndedAt'],
          matchDurationSeconds: matchData['matchDurationSeconds'],
          warmupSeconds: matchData['warmupSeconds'] || 240,
          restBetweenGamesSeconds: matchData['restBetweenGamesSeconds'] || 120,
          bestOf: matchData['bestOf'] || 3,
          pointsToWin: matchData['pointsToWin'] || 11,
          refereeName: matchData['refereeName'],
          gamesDetailed: matchData['gamesDetailed']
        };

        // Cargar datos de jugadores
        return this.getPlayers().pipe(
          map(players => {
            const matchExtended: MatchExtended = { ...match };
            matchExtended.player1 = players.find(p => p.playerId === match.player1Id);
            matchExtended.player2 = players.find(p => p.playerId === match.player2Id);
            return matchExtended;
          })
        );
      }),
      switchMap(result => result || of(null))
    );
  }

  /**
   * Sincroniza el marcador en vivo (parcial) durante el arbitraje.
   * Se llama tras cada punto/let/stroke para que el tablero público vea
   * el marcador actualizándose en tiempo real.
   */
  updateMatchPartial(matchId: string, partial: Partial<Match>): Observable<void> {
    const docRef = doc(this.firestore, `${this.matchesCollection}/${matchId}`);
    return from(updateDoc(docRef, stripUndefined(partial as any))).pipe(
      switchMap(() => of(undefined))
    );
  }

  /**
   * Finaliza el partido con todos los detalles (gamesDetailed, timestamps, etc.)
   * y actualiza la clasificación del torneo
   */
  finalizeMatchWithDetails(matchId: string, matchData: Partial<Match>): Observable<void> {
    const docRef = doc(this.firestore, `${this.matchesCollection}/${matchId}`);
    return from(updateDoc(docRef, { ...matchData })).pipe(
      switchMap(() => {
        console.log('✓ Partido finalizado y guardado en Firestore');
        // Actualizar clasificación del torneo si es necesario
        // (se puede llamar a updateResults si se necesita)
        return of(undefined);
      })
    );
  }

}


